import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import ToolFooter from '../../components/ui/ToolFooter';
import AuthorCredit from '../../components/ui/AuthorCredit';
import FreshnessDate, { getFreshnessDateIso } from '../../components/ui/FreshnessDate';

export default function OnboardingGuide() {
  const [step, setStep] = useState(0);
  const [score, setScore] = useState(0);
  const [complete, setComplete] = useState(false);

  const questions = [
    {
      q: "How does the client currently deliver their records?",
      options: [
        { text: "Carrier bag of crumpled receipts", value: 3 },
        { text: "Messy Excel spreadsheet or emails", value: 2 },
        { text: "Cloud accounting software (Xero, QBO)", value: 0 }
      ]
    },
    {
      q: "What is their attitude towards new apps or technology?",
      options: [
        { text: "Refuses to learn new systems or forgets passwords", value: 3 },
        { text: "Will use it if forced, but complains", value: 2 },
        { text: "Happy to adopt new technology", value: 0 }
      ]
    },
    {
      q: "Do they use WhatsApp to communicate with family or friends?",
      options: [
        { text: "Yes, they use it daily for everything", value: 2 },
        { text: "Sometimes", value: 1 },
        { text: "No, flip phone only", value: -1 }
      ]
    },
    {
      q: "How often do they lose receipts or forget to log mileage?",
      options: [
        { text: "Constantly. Every quarter is a battle.", value: 3 },
        { text: "Occasionally, but we manage.", value: 1 },
        { text: "Rarely, they are very organized.", value: 0 }
      ]
    },
    {
      q: "What is the firm's biggest bottleneck with this client?",
      options: [
        { text: "Chasing them for missing documents", value: 3 },
        { text: "Manual data entry and reconciliation", value: 2 },
        { text: "We don't have a bottleneck with them", value: 0 }
      ]
    }
  ];

  const handleAnswer = (val) => {
    setScore(score + val);
    if (step < questions.length - 1) {
      setStep(step + 1);
    } else {
      setComplete(true);
    }
  };

  const getRecommendation = () => {
    if (score >= 8) {
      return {
        title: "High capture friction: WhatsApp may help",
        desc: "This client is highly tech-averse but uses everyday tools like WhatsApp and email. Do not attempt to force them onto a complex app. Have them forward emails and snap WhatsApp photos to Zap Expenses, then import the structured export into the firm's existing software. Zap does not submit to HMRC.",
        color: "red"
      };
    } else if (score >= 4) {
      return {
        title: "Medium Risk: Hybrid Approach Required",
        desc: "They might struggle with dedicated apps. Setting up an easy capture method like Zap Expenses alongside your core ledger will save your team hours of chasing.",
        color: "amber"
      };
    } else {
      return {
        title: "Low Risk: Continue Current Software",
        desc: "This client is fully digital and comfortable with existing software. Continue using Xero/QBO/FreeAgent.",
        color: "emerald"
      };
    }
  };

  const rec = complete ? getRecommendation() : null;

  const howToSchema = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": "How to Onboard Non-Digital Clients to MTD 2026",
    "dateModified": getFreshnessDateIso(),
    "step": [
      { "@type": "HowToStep", "text": "Identify clients still using paper or spreadsheets." },
      { "@type": "HowToStep", "text": "Deploy a 'No-App' capture method like WhatsApp." },
      { "@type": "HowToStep", "text": "Export structured records for the firm to import into existing compatible software. Zap does not submit to HMRC." }
    ]
  };

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "The Accountant's Toolkit for Tech-Averse MTD Onboarding",
    "description": "A guide for accounting firms to identify tech-averse clients and offer WhatsApp/email receipt capture alongside the firm's existing filing software.",
    "dateModified": getFreshnessDateIso()
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white font-sans selection:bg-indigo-500/30">
      <Helmet>
        <script type="application/ld+json">{JSON.stringify([howToSchema, articleSchema])}</script>
        <title>The Accountant's Toolkit for Tech-Averse MTD Onboarding | Zap Expenses</title>
        <meta name="description" content="The Accountant's Toolkit for MTD 2026. How to onboard tech-averse clients using WhatsApp and email automation." />
        <meta name="keywords" content="MTD onboarding, tech-averse accounting, HMRC 2026 firm margins, WhatsApp receipt capture, digital exclusion myth" />
      </Helmet>

      <div className="max-w-4xl mx-auto py-16 px-6">
        <FreshnessDate className="mb-4" />

        <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight mb-4">
          MTD Onboarding Toolkit:<br />
          <span className="text-indigo-400">The Tech-Averse Client.</span>
        </h1>

        <AuthorCredit />

        <div className="bg-indigo-500/10 border border-indigo-500/30 p-6 rounded-2xl mb-6 shadow-lg">
          <p className="text-xl font-medium leading-relaxed">
            <strong>The greatest risk to accounting firm margins in 2026 is forcing tech-averse tradesmen to install and learn complex general ledger software.</strong>
          </p>
        </div>

        <div className="bg-amber-500/10 border border-amber-500/30 p-4 rounded-xl text-left text-sm text-slate-300 w-full mb-12 shadow-lg">
          <strong className="text-amber-400 block mb-1">The "Reasonable Care" Loss:</strong>
          "HMRC attributes 31% of the UK's £46.8bn tax gap to 'Failure to Take Reasonable Care'—the largest single cause of lost revenue."
          <div className="text-[10px] text-slate-500 italic mt-2 border-t border-amber-500/20 pt-2">
            Source: <a href="https://www.gov.uk/government/statistics/measuring-tax-gaps/7-tax-gaps-illustrative-tax-gap-by-behaviour" target="_blank" rel="noopener noreferrer" className="underline hover:text-amber-400">HMRC, Tax gaps: illustrative tax gap by behaviour</a>
          </div>
        </div>

        <div className="prose prose-invert max-w-none text-slate-300 mb-12">
          <p>
            You must isolate your digital-averse clients from your highly-organized digital clients immediately. Conduct the short diagnostic audit below to detect exactly which clients will consume disproportionate unbillable hours, so you can issue them a zero-friction "WhatsApp-only" workflow.
          </p>
        </div>

        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-8 shadow-2xl">
          {!complete ? (
            <div>
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">Tech-Aversion Audit</h2>
                <span className="text-sm font-bold text-slate-500">Question {step + 1} of {questions.length}</span>
              </div>
              <div className="w-full bg-slate-800 h-2 rounded-full mb-8">
                <div
                  className="bg-indigo-500 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${((step) / questions.length) * 100}%` }}
                ></div>
              </div>

              <h3 className="text-xl font-semibold mb-6">{questions[step].q}</h3>
              <div className="space-y-3">
                {questions[step].options.map((opt, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleAnswer(opt.value)}
                    className="w-full text-left px-6 py-4 bg-black/30 hover:bg-indigo-500/10 border border-white/5 hover:border-indigo-500/50 rounded-xl transition-all font-medium text-slate-200 hover:text-white"
                  >
                    {opt.text}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className={`p-8 rounded-2xl border ${rec.color === 'red' ? 'bg-red-500/10 border-red-500/30' :
                rec.color === 'amber' ? 'bg-amber-500/10 border-amber-500/30' :
                  'bg-emerald-500/10 border-emerald-500/30'
              }`}>
              <span className={`text-xs font-bold uppercase tracking-widest px-3 py-1 rounded inline-block mb-4 ${rec.color === 'red' ? 'bg-red-500 text-white' :
                  rec.color === 'amber' ? 'bg-amber-500 text-black' :
                    'bg-emerald-500 text-white'
                }`}>
                Audit Result
              </span>
              <h3 className="text-3xl font-extrabold text-white mb-3">{rec.title}</h3>
              <p className="text-lg text-slate-300 mb-6">{rec.desc}</p>

              <div className="flex flex-col sm:flex-row gap-4 mt-8">
                <button
                  onClick={() => { setStep(0); setScore(0); setComplete(false); }}
                  className="px-6 py-3 border border-white/10 hover:bg-white/5 rounded-xl font-semibold text-sm transition-all"
                >
                  Audit Another Client
                </button>
                {(rec.color === 'red' || rec.color === 'amber') && (
                  <a href="mailto:partners@zapexpenses.com" className="px-6 py-3 bg-white text-black hover:bg-slate-200 rounded-xl font-semibold text-sm transition-all text-center">
                    Book a Partner Demo
                  </a>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="mt-16">
          <h3 className="text-2xl font-bold mb-6">How to Onboard Non-Digital Clients to MTD 2026</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-slate-900 border border-white/5 p-6 rounded-xl">
              <div className="w-8 h-8 bg-indigo-500/20 text-indigo-400 flex items-center justify-center rounded-full font-bold mb-4">1</div>
              <h4 className="font-bold text-lg mb-2">Identify</h4>
              <p className="text-slate-400 text-sm">Find clients still using paper bags or dense spreadsheets. Segment them strictly.</p>
            </div>
            <div className="bg-slate-900 border border-white/5 p-6 rounded-xl">
              <div className="w-8 h-8 bg-indigo-500/20 text-indigo-400 flex items-center justify-center rounded-full font-bold mb-4">2</div>
              <h4 className="font-bold text-lg mb-2">Deploy 'No-App' Capture</h4>
              <p className="text-slate-400 text-sm">Issue them a Zap Expenses WhatsApp number and dedicated forwarding email.</p>
            </div>
            <div className="bg-slate-900 border border-white/5 p-6 rounded-xl">
              <div className="w-8 h-8 bg-indigo-500/20 text-indigo-400 flex items-center justify-center rounded-full font-bold mb-4">3</div>
              <h4 className="font-bold text-lg mb-2">Export for the firm</h4>
              <p className="text-slate-400 text-sm">The firm imports structured records into existing compatible software. Zap does not submit to HMRC.</p>
            </div>
          </div>
        </div>

        <div id="stat-leakage-2026" className="mt-10 mb-2 bg-black/40 border border-slate-800 p-6 rounded-xl shadow-inner relative overflow-hidden text-center md:text-left">
          <p className="text-emerald-400 font-bold uppercase tracking-widest text-xs mb-1">The Digital Exemption Myth</p>
          <p className="text-2xl font-extrabold text-white mb-2">47% Exemption Rejection Rate</p>
          <p className="text-slate-400 text-sm">
            HMRC continues to reject approx. 47% of digital exclusion claims, applying a strict 'Functional Inability' test.
          </p>
          <p className="mt-3 text-[10px] text-slate-500 italic border-t border-slate-800/50 pt-2">
            Source: <a href="https://www.cityam.com/hmrc-rejects-nearly-half-of-digital-tax-exemptions/" target="_blank" rel="noopener noreferrer" className="underline hover:text-emerald-400 transition-colors">FOI Release 2024/052; Accountancy Age Analysis</a>
          </p>
        </div>

        <div className="mt-6 mb-2 text-center">
          <a href="https://www.gov.uk/government/publications/making-tax-digital/overview-of-making-tax-digital" target="_blank" rel="noopener noreferrer" className="text-sm font-bold text-indigo-400 hover:text-indigo-300 underline">
            Review the Official MTD Overview on Gov.uk →
          </a>
        </div>


      </div>
    </div>
  );
}
